import { useState, useEffect, useCallback } from "react";
import apiClient from "../api/client";

const BASE_URL = "https://profitree-backend-9482feeffc4a.herokuapp.com";

const endpointMap = {
  'transactions/getPastWashSaleTransactions':    '/api/v1/transactions/wash-sales',
  'transactions/getWashSaleAvoidanceCompanies':  '/api/v1/transactions/wash-sales',
  'transactions/getCurrentYearTaxableProfit':    '/api/v1/tax/realized?type=gains',
  'transactions/getCurrentYearTaxableLoss':      '/api/v1/tax/realized?type=losses',
  'transactions/getPotentialLossOpportunities':  '/api/v1/tax/harvest/opportunities?type=loss',
  'transactions/getPotentialGainOpportunities':  '/api/v1/tax/harvest/opportunities?type=gain',
  // size=10000 loads all items; paginate properly when needed
  'transactions/getSettledTransactions':         '/api/v1/transactions/settled?page=0&size=10000',
  'transactions/getAccountSyncStatus':           '/api/v1/accounts/sync-status',
  'transactions/getManualRefreshData':           '/api/v1/portfolio/refresh',
  // size=10000 loads all items; paginate properly when needed
  'transactions/getCurrentPortfolio':            '/api/v1/portfolio?page=0&size=10000',
};

const CACHE_TTL_MS = 60 * 60 * 1000;

const cacheStore = new Map();
const pendingFetches = new Map();

const getCacheKey = (endpoint) => {
  const path = endpointMap[endpoint] || endpoint;
  return path.startsWith('http') ? path : `${BASE_URL}${path}`;
};

const isCacheValid = (cacheEntry) => {
  if (!cacheEntry || cacheEntry.invalidated) return false;
  return (Date.now() - cacheEntry.timestamp) < CACHE_TTL_MS;
};

const getCachedData = (endpoint) => {
  const key = getCacheKey(endpoint);
  const entry = cacheStore.get(key);
  if (isCacheValid(entry)) return entry.data;
  if (entry) cacheStore.delete(key);
  return null;
};

export const hasCachedData = (endpoint) => getCachedData(endpoint) !== null;

export const getCachedDataForEndpoint = (endpoint) => getCachedData(endpoint);

const setCachedData = (endpoint, data) => {
  cacheStore.set(getCacheKey(endpoint), { data, timestamp: Date.now(), invalidated: false });
};

export const invalidateCache = (endpoint = null) => {
  if (endpoint) {
    cacheStore.delete(getCacheKey(endpoint));
  } else {
    cacheStore.clear();
  }
};

export const invalidateAllTransactionCaches = () => {
  Object.keys(endpointMap).forEach(invalidateCache);
  cacheStore.clear();
};

export const useTransactions = (endpoint, shouldFetch = true) => {
  const [data, setData] = useState(() => {
    if (!shouldFetch) return [];
    const cached = getCachedData(endpoint);
    return cached !== null ? cached : [];
  });

  const [loading, setLoading] = useState(() => {
    if (!shouldFetch) return false;
    return getCachedData(endpoint) === null;
  });

  const [error, setError] = useState(null);

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!shouldFetch) { setLoading(false); return; }

    if (!forceRefresh) {
      const cached = getCachedData(endpoint);
      if (cached !== null) { setData(cached); setLoading(false); return; }
    }

    const cacheKey = getCacheKey(endpoint);
    const existing = pendingFetches.get(cacheKey);
    if (existing && !forceRefresh) {
      try {
        const result = await existing;
        setData(result);
        setLoading(false);
        return;
      } catch {
        pendingFetches.delete(cacheKey);
      }
    }

    setLoading(true);
    setError(null);

    const fetchPromise = (async () => {
      try {
        const path = endpointMap[endpoint] || endpoint;
        const fullUrl = path.startsWith('http') ? path : `${BASE_URL}${path}`;
        const response = await apiClient.get(fullUrl);
        // Unwrap paginated envelope if present
        const raw = response.data;
        const result = Array.isArray(raw?.items) ? raw.items : raw;
        setCachedData(endpoint, result);
        return result;
      } catch (err) {
        if (err.response?.status !== 401) {
          setError(err.message || "An error occurred");
        }
        throw err;
      } finally {
        pendingFetches.delete(cacheKey);
      }
    })();

    if (!forceRefresh) pendingFetches.set(cacheKey, fetchPromise);

    try {
      const result = await fetchPromise;
      if (result !== null) setData(result);
    } catch {
      // error already set inside fetchPromise
    } finally {
      setLoading(false);
    }
  }, [endpoint, shouldFetch]);

  useEffect(() => {
    if (shouldFetch && getCachedData(endpoint) === null) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, shouldFetch]);

  const refetch = useCallback(() => fetchData(true), [fetchData]);

  return { data, loading, error, refetch };
};